import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, AlertTriangle } from 'lucide-react';

const RULES = [
  {
    n: 1,
    title: 'Respectful communication only',
    body: 'Harassment, hate speech, or explicit unsolicited content will result in immediate account suspension.',
  },
  {
    n: 2,
    title: 'No fake profiles or impersonation',
    body: 'Verified accounts only. Violations result in a permanent ban with no appeal.',
  },
  {
    n: 3,
    title: 'No sharing personal contact info publicly',
    body: 'Do not put phone numbers, emails, or addresses in your public profile fields. Save it for private chat once you trust the match.',
  },
  {
    n: 4,
    title: 'No nudity or sexually explicit photos of any kind',
    body: 'Profile media must be safe-for-everyone. Nudity or explicit imagery is not permitted anywhere in the dating section.',
  },
  {
    n: 5,
    title: 'No inappropriate or explicit content',
    body: 'Sending or posting inappropriate/explicit content will result in immediate account suspension.',
  },
  {
    n: 6,
    title: 'You must be 18 or older',
    body: 'The dating section is 18+ only. Age is verified during profile setup — anyone under 18 is blocked from accessing dating features entirely.',
  },
  {
    n: 7,
    title: 'Reported profiles are reviewed within 24 hours',
    body: 'Our admin team reviews every report. If a report is valid, we take action fast.',
  },
  {
    n: 8,
    title: 'Three reports trigger an automatic suspension',
    body: 'If three unique members report the same account, it is temporarily suspended pending admin review.',
  },
  {
    n: 9,
    title: 'Permanent bans for serious violations — no appeal',
    body: 'Serious violations (harassment, fake profiles, explicit content, threats) result in permanent bans that cannot be appealed.',
  },
];

export default function DatingRules() {
  return (
    <div className="bg-white text-black rounded-2xl p-4 sm:p-6 -mx-2 sm:mx-0 max-w-3xl mx-auto space-y-6" data-testid="dating-rules-page">
      <div>
        <Link to="/dating" data-testid="dating-rules-back"
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-black transition-colors">
          <ArrowLeft size={14} /> Back to Dating
        </Link>
      </div>

      <header className="rounded-2xl bg-white border border-gray-200 p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center shrink-0">
            <ShieldCheck className="text-green-600" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-black">Dating Community Rules</h1>
            <p className="text-sm text-gray-600 mt-1">
              PBB Dating is a safe, respectful, adults-only space. By using this section you agree to follow these rules. Breaking them can suspend or permanently ban your dating access.
            </p>
          </div>
        </div>
      </header>

      <ul className="space-y-3" data-testid="dating-rules-list">
        {RULES.map(r => (
          <li key={r.n} className="rounded-xl bg-white border border-gray-200 p-5 shadow-sm flex gap-4">
            <div className="w-9 h-9 rounded-full bg-black text-white font-bold text-sm flex items-center justify-center shrink-0">
              {r.n}
            </div>
            <div>
              <div className="font-semibold text-black">{r.title}</div>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">{r.body}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-5 flex gap-3">
        <AlertTriangle className="text-yellow-600 shrink-0 mt-0.5" size={18} />
        <div className="text-sm text-yellow-900">
          <div className="font-semibold">See something? Report it.</div>
          <p className="mt-1">
            Tap the flag icon on any profile or match. Our team reviews every report within 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
}
